import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getOrders } from '../../services/adminService';

const formatPrice = (amount, currency = 'mxn') =>
  new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount || 0);

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('es-MX', { dateStyle: 'medium' });

const STATUS_OPTIONS = [
  { value: '', label: 'Todos los estados' },
  { value: 'confirmed',      label: 'Confirmado' },
  { value: 'en_preparacion', label: 'En preparación' },
  { value: 'recoleccion',    label: 'Recolección' },
  { value: 'enviado',        label: 'Enviado' },
  { value: 'entregado',      label: 'Entregado' },
];

const STATUS_CONFIG = {
  awaiting_payment: { label: 'Esperando pago', color: '#8e44ad', bg: '#f4ecf7' },
  confirmed:      { label: 'Confirmado',     color: '#2ca9e1', bg: '#e8f6fd' },
  en_preparacion: { label: 'En preparación', color: '#f0a500', bg: '#fef9e7' },
  recoleccion:    { label: 'Recolección',     color: '#6f42c1', bg: '#f1ecf9' },
  enviado:        { label: 'Enviado',         color: '#e8640c', bg: '#fdf2ec' },
  entregado:      { label: 'Entregado',       color: '#28a745', bg: '#eaf7ed' },
  cancelled:      { label: 'Cancelado',       color: '#dc3545', bg: '#fdecea' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status, color: '#666', bg: '#f0f0f0' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: '12px',
      fontSize: '0.8rem',
      fontWeight: '600',
      color: cfg.color,
      background: cfg.bg,
    }}>
      {cfg.label}
    </span>
  );
};

const inputStyle = {
  padding: '0.5rem 0.75rem',
  border: '1px solid #ddd',
  borderRadius: '8px',
  fontSize: '0.9rem',
  outline: 'none',
};

const PAGE_LIMIT = 20;

const AdminOrderList = () => {
  const { session } = useAuth();
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Debounce search input 400 ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [status]);

  useEffect(() => {
    if (!session?.access_token) {
      setLoading(false);
      setError('No hay sesión activa.');
      return;
    }
    let cancelled = false;
    setLoading(true);

    const params = { page, limit: PAGE_LIMIT };
    if (status) params.status = status;
    if (debouncedSearch) params.search = debouncedSearch;

    getOrders(session.access_token, params)
      .then(data => {
        if (cancelled) return;
        setOrders(data.orders || []);
        setTotal(data.total || 0);
        setTotalPages(data.totalPages || 1);
        setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [session?.access_token, page, status, debouncedSearch]);

  return (
    <div className="container" style={{ padding: '2rem 0 4rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
        <Link to="/admin" style={{ color: '#888', textDecoration: 'none', fontSize: '0.9rem' }}>← Panel</Link>
        <h1 style={{ fontFamily: "'Gambado Sans', sans-serif", fontSize: '1.6rem', color: '#333', margin: 0 }}>
          Pedidos
        </h1>
        <span style={{ color: '#888', fontSize: '0.9rem', marginLeft: 'auto' }}>
          {total} pedido{total !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', margin: '1.5rem 0' }}>
        <input
          type="text"
          placeholder="Buscar por email o nombre..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ ...inputStyle, minWidth: '220px', flex: '1' }}
        />
        <select
          value={status}
          onChange={e => setStatus(e.target.value)}
          style={{ ...inputStyle, cursor: 'pointer' }}
        >
          {STATUS_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Error */}
      {error && <p style={{ color: '#dc3545', marginBottom: '1rem' }}>{error}</p>}

      {/* Table */}
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ background: '#fafafa' }}>
                {['#', 'Fecha', 'Cliente', 'Artículos', 'Total', 'Estado', ''].map(h => (
                  <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', color: '#666', fontWeight: '600', whiteSpace: 'nowrap', borderBottom: '1px solid #f0f0f0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} style={{ padding: '2.5rem', textAlign: 'center', color: '#888' }}>Cargando...</td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: '2.5rem', textAlign: 'center', color: '#888' }}>
                    No se encontraron pedidos.
                  </td>
                </tr>
              ) : orders.map(order => {
                const items = Array.isArray(order.items) ? order.items : [];
                const itemSummary = items.length === 1
                  ? items[0].name || '1 artículo'
                  : `${items.length} artículos`;
                return (
                  <tr key={order.id} style={{ borderBottom: '1px solid #f8f8f8' }}>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      {order.orderCode
                        ? <><span style={{ fontWeight: '600', color: '#333', fontSize: '0.85rem' }}>{order.orderCode}</span><br/><span style={{ color: '#bbb', fontSize: '0.75rem' }}>#{order.id}</span></>
                        : <span style={{ color: '#888', fontSize: '0.85rem' }}>#{order.id}</span>
                      }
                    </td>
                    <td style={{ padding: '0.75rem 1rem', whiteSpace: 'nowrap', color: '#555' }}>{formatDate(order.createdAt)}</td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <div style={{ fontWeight: '500' }}>{order.customerName || '—'}</div>
                      <div style={{ color: '#888', fontSize: '0.82rem' }}>{order.customerEmail}</div>
                    </td>
                    <td style={{ padding: '0.75rem 1rem', color: '#555', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {itemSummary}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', fontWeight: '600', color: '#E4405F', whiteSpace: 'nowrap' }}>
                      {formatPrice(order.totalAmount, order.currency)}
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <StatusBadge status={order.fulfillmentStatus} />
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <Link
                        to={`/admin/orders/${order.id}`}
                        style={{ color: '#2ca9e1', textDecoration: 'none', fontWeight: '500', fontSize: '0.85rem', whiteSpace: 'nowrap' }}
                      >
                        Ver →
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '1.5rem', justifyContent: 'center' }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #ddd',
              borderRadius: '8px',
              background: page === 1 ? '#f8f9fa' : 'white',
              color: page === 1 ? '#aaa' : '#333',
              cursor: page === 1 ? 'default' : 'pointer',
              fontSize: '0.9rem',
            }}
          >
            ← Anterior
          </button>
          <span style={{ color: '#666', fontSize: '0.9rem' }}>
            Página {page} de {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #ddd',
              borderRadius: '8px',
              background: page === totalPages ? '#f8f9fa' : 'white',
              color: page === totalPages ? '#aaa' : '#333',
              cursor: page === totalPages ? 'default' : 'pointer',
              fontSize: '0.9rem',
            }}
          >
            Siguiente →
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminOrderList;
